/**
 * Tiny structured logger for the citizen PWA.
 *
 * Mirrors the JSON shape that the api emits via pino so that anything
 * we forward to Cloud Logging (later, when we add a remote sink) lines
 * up. Each call writes one line — no batching, no buffer, no formatter
 * tree. In dev we log via `console.{info,warn,error}` so the browser
 * devtools still groups + colours nicely.
 */
type Level = 'debug' | 'info' | 'warn' | 'error';

type LogContext = Record<string, unknown>;

const IS_PROD = import.meta.env.PROD;

function emit(level: Level, msg: string, ctx?: LogContext): void {
  if (IS_PROD) {
    const line = JSON.stringify({
      level,
      time: new Date().toISOString(),
      msg,
      app: 'mycity-app',
      ...ctx,
    });
    if (level === 'error') console.error(line);
    else if (level === 'warn') console.warn(line);
    else console.log(line);
    return;
  }
  // Dev: leverage the browser console's structured display.
  const fn =
    level === 'error'
      ? console.error
      : level === 'warn'
        ? console.warn
        : level === 'debug'
          ? console.debug
          : console.info;
  if (ctx) fn(`[${level}] ${msg}`, ctx);
  else fn(`[${level}] ${msg}`);
}

export const logger = {
  debug: (msg: string, ctx?: LogContext): void => emit('debug', msg, ctx),
  info: (msg: string, ctx?: LogContext): void => emit('info', msg, ctx),
  warn: (msg: string, ctx?: LogContext): void => emit('warn', msg, ctx),
  error: (msg: string, ctx?: LogContext): void => emit('error', msg, ctx),
};
